import { useCallback, useRef, type ReactNode } from "react";
import { Sobreposicao } from "../../components/Sobreposicao.js";

/**
 * A moldura dos painéis de impressão: título, "Fechar" à vista, corpo e um rodapé que fica colado na
 * borda de baixo quando o conteúdo passa da altura da tela.
 *
 * `Sobreposicao` não tem botão de fechar — só o toque no fundo e o Esc, e no celular o fundo é uma
 * faixa de poucos pixels. Daí o "Fechar" no cabeçalho, com 44 px.
 *
 * O `aoFechar` que a `Sobreposicao` recebe é **estável** de propósito: o efeito dela depende dele, e a
 * cada mudança ele devolve o foco a quem abriu o painel e o puxa de volta para o painel — com um campo
 * de texto aberto, o foco sairia do campo a cada letra digitada. Por isso a função passa por uma ref.
 */
export function MolduraDoPainel({
  titulo,
  subtitulo,
  aoFechar,
  rodape,
  children,
}: {
  titulo: string;
  subtitulo?: string | undefined;
  aoFechar: () => void;
  rodape?: ReactNode;
  children: ReactNode;
}) {
  const ultimo = useRef(aoFechar);
  ultimo.current = aoFechar;
  const fechar = useCallback(() => ultimo.current(), []);

  return (
    <Sobreposicao rotulo={titulo} aoFechar={fechar}>
      <div className="flex flex-col">
        <header className="flex items-start justify-between gap-3 border-b border-borda px-4 py-3 sm:px-6">
          <div className="min-w-0 py-1">
            <h2 className="font-display text-[1.25rem] leading-tight tracking-[-0.01em]">
              {titulo}
            </h2>
            {subtitulo && (
              <p className="mt-0.5 text-[0.8125rem] leading-snug text-texto-suave">{subtitulo}</p>
            )}
          </div>
          <button
            type="button"
            onClick={fechar}
            className="-mr-2 inline-flex min-h-11 shrink-0 items-center rounded-peca px-3 text-[0.9375rem] text-texto-suave transition-colors hover:bg-superficie hover:text-texto"
          >
            Fechar
          </button>
        </header>

        <div className="flex flex-col gap-5 px-4 py-5 sm:px-6">{children}</div>

        {rodape && (
          <footer className="area-segura-inferior sticky bottom-0 border-t border-borda bg-fundo px-4 pt-3 sm:px-6">
            {rodape}
          </footer>
        )}
      </div>
    </Sobreposicao>
  );
}

/** Um recado dentro de um painel: aviso (brasa) ou impedimento (descartar). */
export function Nota({
  tom,
  id,
  children,
}: {
  tom: "aviso" | "impedimento";
  id?: string;
  children: ReactNode;
}) {
  return (
    <p
      id={id}
      className={
        tom === "impedimento"
          ? "rounded-peca border border-descartar/40 bg-descartar/10 px-3 py-2.5 text-[0.875rem] leading-relaxed text-texto"
          : "rounded-peca border border-brasa/30 bg-brasa-fraca/40 px-3 py-2.5 text-[0.875rem] leading-relaxed text-texto"
      }
    >
      {children}
    </p>
  );
}
